import { inject, Injectable } from '@angular/core';

import {
  externalCommonAspURL,
  externalNetcoreURL,
  isItValidExternalModuleToOpenInTheModal,
  openAspRegistryModalByAppId,
  openNetCoreRegistryModalByAppId,
  simpleRedirectURL,
} from 'projects/lib-shared-common/src/public-api';

import { JsonCallbackCall, JsonCallbackValue, parseJsonCallback } from './json-callback-parser';
import { NotificationIntegrationService } from './notification-integration.service';

/**
 * What opening a `jsonCallback` did: `modal` opened the record over the page, `redirected` went to another page,
 * `none` the callback was empty, unreadable or not an allowed action, so nothing ran.
 */
export type JsonCallbackResult = 'modal' | 'redirected' | 'none';

export type JsonCallbackAspParams = Parameters<typeof externalCommonAspURL>[1] & { redirectId?: number | string };

/** The record a callback points at, appended to the callback arguments by the comment feed and the RCA links. */
export interface JsonCallbackRecord {
  registryId?: number | string | null;
  applicationId?: string;
}

export interface JsonCallbackOptions {
  /** When set, ASP and NetCore records of modules that support it open in their modal instead of a new page. */
  record?: JsonCallbackRecord;
  /** Opens action plan links on their feed tab (`modalTab=feed`). */
  openOnFeedTab?: boolean;
}

const ALLOWED_ACTIONS = ['openAngularDialog', 'redirectDynamicPageAsp', 'RedirectDynamicPage'] as const;

type AllowedAction = (typeof ALLOWED_ACTIONS)[number];

/**
 * Opens the destination of a backend `jsonCallback`: an https URL (the notification iframe for `/notification/`
 * paths, a plain redirect otherwise) or one of the allowed actions. The callback is parsed as data and
 * dispatched by exact action name; it is never evaluated, and anything outside the allowed list is ignored.
 */
@Injectable({ providedIn: 'root' })
export class JsonCallbackService {
  private readonly _notificationIntegration = inject(NotificationIntegrationService);

  public open(jsonCallback: string | null | undefined, options: JsonCallbackOptions = {}): JsonCallbackResult {
    const callback = jsonCallback?.trim();
    if (!callback) return 'none';
    if (callback.startsWith('https')) return this.openUrl(callback, options.openOnFeedTab);
    const call = parseJsonCallback(callback);
    if (!isAllowedAction(call)) return 'none';
    return this.dispatch(call.name, withRecordArgs(call.args, options.record));
  }

  public redirectAsp(url: string, params: JsonCallbackAspParams, customRedirectId?: number | string | null, applicationId?: string): JsonCallbackResult {
    if (applicationId && isItValidExternalModuleToOpenInTheModal(applicationId)) {
      openAspRegistryModalByAppId((customRedirectId ?? params?.redirectId) as number, applicationId);
      return 'modal';
    }
    externalCommonAspURL(url, params);
    return 'redirected';
  }

  public redirectNetCore(url: string, params: string, customRedirectId?: number | string | null, applicationId?: string): JsonCallbackResult {
    if (applicationId && isItValidExternalModuleToOpenInTheModal(applicationId)) {
      const environmentUrl = url.split('.com')[0] + '.com';
      openNetCoreRegistryModalByAppId(url.replace(environmentUrl, ''), params, customRedirectId as number, applicationId);
      return 'modal';
    }
    externalNetcoreURL(url, params);
    return 'redirected';
  }

  private openUrl(url: string, openOnFeedTab = false): JsonCallbackResult {
    if (url.includes('/notification/')) {
      this._notificationIntegration.openIframeByPath(url);
      return 'modal';
    }
    simpleRedirectURL(openOnFeedTab ? markFeedTab(url) : url);
    return 'redirected';
  }

  private dispatch(action: AllowedAction, args: JsonCallbackValue[]): JsonCallbackResult {
    switch (action) {
      case 'openAngularDialog':
        this._notificationIntegration.openIframeByPath(asText(args[0]));
        return 'modal';
      case 'redirectDynamicPageAsp':
        return this.redirectAsp(asText(args[0]), args[1] as JsonCallbackAspParams, asId(args[2]), asOptionalText(args[3]));
      case 'RedirectDynamicPage':
        return this.redirectNetCore(asText(args[0]), asText(args[1]), asId(args[2]), asOptionalText(args[3]));
    }
  }
}

function isAllowedAction(call: JsonCallbackCall | null): call is JsonCallbackCall & { name: AllowedAction } {
  return !!call && (ALLOWED_ACTIONS as readonly string[]).includes(call.name);
}

function withRecordArgs(args: JsonCallbackValue[], record: JsonCallbackRecord | undefined): JsonCallbackValue[] {
  if (!record) return args.slice(0, 2);
  return [...args, record.registryId ?? null, record.applicationId ?? null];
}

function asText(value: JsonCallbackValue | undefined): string {
  return typeof value === 'string' ? value : '';
}

function asOptionalText(value: JsonCallbackValue | undefined): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

function asId(value: JsonCallbackValue | undefined): number | string | null {
  return typeof value === 'number' || typeof value === 'string' ? value : null;
}

function markFeedTab(url: string): string {
  if (!url.includes('actionPlanUniqueId=') || url.includes('modalTab=')) return url;
  return `${url}${url.includes('?') ? '&' : '?'}modalTab=feed`;
}
